import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSolana } from '../../../hooks/useSolana';
import { toast } from 'react-toastify';
import SendingStatus from '../SendingStatus';
import { useGetTokenPriceQuery } from '../../../redux/api/public/tokens';
import { openModal } from '../../../redux/slices/modals';
import {
  usePrepareHTLCClaimMutation as usePrepareHTLCClaimMutationAuth,
  useBroadcastClaimHTLCMutation as useBroadcastClaimHTLCMutationAuth,
} from '../../../redux/api/authorized/gifts';
import { useOutletContext } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import { useSelector } from 'react-redux';
import { selectPrivyToken } from '../../../redux/slices/auth';
import { fromTokenAmount } from '../../../hooks/useSolana';
import { formatTokenPrice } from '../../../common/currency';
import PresentationalGift from './PresentationalGift';
import useCustomLogin from '../../../hooks/useCustomLogin';
import WidthContainer from '../../../components/WidthContainer';
import { formatTokenAmountForDisplay } from '../../../hooks/useGiftAmountCalculations';

const ClaimHTLCGift = () => {
  const { gift, token, isCreator, isReceiver } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [claimError, setClaimError] = useState(null);
  const navigate = useNavigate();
  const {
    signAndSerializeTransactionWithWallet,
    embeddedWallet,
    externalWallet,
    walletsReady,
  } = useSolana();
  const [sendingStatus, setSendingStatus] = useState('idle');
  const dispatch = useDispatch();

  // Calculate token amount and USD value
  const tokenAmount = fromTokenAmount(gift?.tokenAmount, token?.decimals);
  const formattedTokenAmount = formatTokenAmountForDisplay(tokenAmount);

  // Check if amount was specified in USD or token
  const amountSpecifiedInUsd = gift?.amountSpecifiedInUsd ?? true;

  // Extract claim code from URL query parameter
  const claimCode = searchParams.get('code');

  const { user, authenticated } = usePrivy();
  const privyToken = useSelector(selectPrivyToken);
  const { login } = useCustomLogin();

  // HTLC claim mutations - always use authenticated versions since claiming requires auth
  const [prepareHTLCClaim] = usePrepareHTLCClaimMutationAuth();
  const [broadcastClaimHTLC] = useBroadcastClaimHTLCMutationAuth();

  const { data: tokenPrice } = useGetTokenPriceQuery(token?.address, {
    skip: !token,
    pollingInterval: 10000,
  });

  const tokenPriceUSD = tokenPrice?.priceUSDC;

  // Calculate current USD value based on token price
  const currentUsdValue =
    tokenPriceUSD && tokenAmount ? tokenPriceUSD * parseFloat(tokenAmount) : 0;

  const handleGiftClaimed = () => {
    dispatch(
      openModal({
        name: 'giftClaimed',
        props: {
          gift: gift,
        },
      }),
    );
  };

  // Handle auto-claim after OAuth login
  useEffect(() => {
    const shouldAutoClaim = searchParams.get('autoClaim');
    if (
      shouldAutoClaim === 'true' &&
      authenticated &&
      privyToken &&
      walletsReady &&
      sendingStatus === 'idle'
    ) {
      // Clean up the query parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('autoClaim');
      setSearchParams(newSearchParams, { replace: true });

      // Trigger the claim
      handleClaim();
    }
  }, [authenticated, privyToken, searchParams, sendingStatus, walletsReady]);

  // Auto-trigger gift claimed modal when successfully claimed
  useEffect(() => {
    if (sendingStatus === 'success') {
      handleGiftClaimed();
    }
  }, [sendingStatus]);

  const handleClaim = async () => {
    if (!authenticated || !privyToken) {
      // Add query parameter to track claim intent across OAuth redirect
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('autoClaim', 'true');
      setSearchParams(newSearchParams, { replace: true });

      login();
    } else {
      // Validate that we have the required claim code
      if (!claimCode) {
        toast.error('Claim code is required. Please check the gift link.');
        return;
      }

      try {
        setClaimError(null);
        setSendingStatus('sending');

        const walletToUse = externalWallet || embeddedWallet;

        // Step 1: Prepare the claim transaction
        const prepareResult = await prepareHTLCClaim({
          giftId: gift.id,
          claimCode,
          sourceWallet: walletToUse.address,
        }).unwrap();

        // Step 2: Sign the transaction with user's wallet
        const signedTransaction = await signAndSerializeTransactionWithWallet(
          prepareResult.serializedTransaction,
          walletToUse,
        );

        // Step 3: Broadcast the claim transaction
        await broadcastClaimHTLC({
          giftId: gift.id,
          claimCode,
          signedTransaction,
          sourceWallet: walletToUse.address,
        }).unwrap();

        setSendingStatus('success');
      } catch (err) {
        console.log(err);
        const errorMessage = err.data?.message || 'Failed to claim gift';
        setClaimError(errorMessage);
        setSendingStatus('error');
      }
    }
  };

  if (!gift) {
    return <p>No gift data available</p>;
  }

  if (!claimCode) {
    return (
      <WidthContainer>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <h2>Invalid Gift Link</h2>
          <p>
            This gift link is missing the claim code. Please check the link and
            try again.
          </p>
        </div>
      </WidthContainer>
    );
  }
  // Show preview phase
  if (sendingStatus === 'idle') {
    // Determine if gift can be claimed
    const canClaimGift = gift?.status === 'HTLC_CREATED';

    const getButtonText = () => {
      if (!user) {
        return 'Get your Gift';
      }
      if (!canClaimGift) {
        return 'See Portfolio';
      }
      if (!claimCode) {
        return 'See Portfolio';
      }
      return 'Get your Gift';
    };

    const handleButtonClick = () => {
      const isClaimed = ['HTLC_CLAIMED', 'CLAIMED'].includes(gift?.status);

      if (isClaimed && !isReceiver) {
        // Redirect to discover page for claimed gifts that aren't theirs
        navigate('/app/discover');
        return;
      }

      if (!canClaimGift || !claimCode) {
        // If gift can't be claimed, just navigate away
        navigate('/');
        return;
      }
      // Otherwise, proceed with normal claim flow
      handleClaim();
    };

    return (
      <PresentationalGift
        usdAmount={amountSpecifiedInUsd ? gift?.purchaseUsdValue : undefined}
        tokenSymbol={token?.symbol || 'CRYPTO'}
        tokenAmount={!amountSpecifiedInUsd ? formattedTokenAmount : formatTokenAmountForDisplay(tokenAmount)}
        token={token}
        giftCardImageUrl={gift?.imageUrl}
        message={gift?.message}
        onClick={handleButtonClick}
        buttonText={getButtonText()}
        status={gift?.status}
        htlcExpiry={gift?.htlcExpiry}
        showStatus={true}
        animated={true}
        giftId={gift?.id}
        isCreator={isCreator}
        isReceiver={isReceiver}
      />
    );
  }

  return (
    <SendingStatus
      giftUrl={gift?.claimUrl}
      usdAmount={amountSpecifiedInUsd ? gift?.purchaseUsdValue : undefined}
      email={gift?.receiver?.email}
      token={token}
      cardImage={gift.imageUrl}
      isSuccess={sendingStatus === 'success'}
      isError={sendingStatus === 'error'}
      tokenAmount={!amountSpecifiedInUsd ? formattedTokenAmount : formatTokenAmountForDisplay(tokenAmount)}
      successText='Your gift has been claimed! '
      errorText={claimError || 'Failed to claim gift'}
      successButtonText='See Portfolio'
      errorButtonText='Try Again'
      successAction={handleGiftClaimed}
      errorAction={() => setSendingStatus('idle')}
      loadingText='Claiming gift...'
    />
  );
};

export default ClaimHTLCGift;
